import { DOCUMENT } from '@angular/common';
import { HttpRequest } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Component as BrComponent, Page } from '@bloomreach/spa-sdk';
import { Logger } from '@utils/logger';
import {
  BehaviorSubject,
  forkJoin,
  fromEvent,
  merge,
  Observable,
  ReplaySubject,
  Subject,
} from 'rxjs';
import { first, map, takeUntil } from 'rxjs/operators';

const logger = Logger.getLogger('AsyncContentManagerService');

export interface ComponentLoaded {
  overview: boolean;
  portfolio: boolean;
  performance: boolean;
  pricing: boolean;
  distributions: boolean;
  documents: boolean;
  ['press-releases']: boolean;
}

/**
 * this list is of the Bloomreach ids of async components
 * Corresponds to BrComponent.getName() values
 * NB: it will need to be updated manually every time an async component is developed
 */
const ASYNC_COMPONENTS: string[] = [
  'holdings',
  'top-ten-holdings',
  'top-ten-holdings-money-funds',
  'asset-allocation',
  'geographic-breakdown',
  'sector-allocation',
  'currency-breakdown',
  'market-capitalisation-breakdown',
  'portfolio-exposure',
  'strategy-breakdown',
  'asset-class-exposure',
  'average-portfolio-exposure',
  'fund-measures',
  'geographic-exposure',
  'sector-exposure',
  'security-type-exposure',
  'portfolio-risk-and-return-analysis',
  'top-long-exposures',
  'top-short-exposures',
  'manager-roster',
  'maturity-allocation',
  'quality-allocation',
  'fund-portfolio',
];

export enum RenderingStatus {
  READY = 'READY',
  IN_PROGRESS = 'IN_PROGRESS',
}

export interface AsyncComponent {
  id: string;
  name: string;
  isAsync: boolean;
  status: RenderingStatus;
  sectionId?: string;
  startTime?: number;
  readyTime?: number;
  duration?: number;
}

/**
 * This service monitors components that render async e.g. they wait until data is loaded before rendering
 * It's used to manage things like initial #fragment scrolling
 * It should be injected into each async component, which will then call methods when status changes
 */
@Injectable({ providedIn: 'root' })
export class AsyncContentManagerService implements OnDestroy {
  private unsubscribe$: Subject<void> = new Subject<void>();

  // stream containing an array representing the BR components currently on the page
  private pageComponents$: ReplaySubject<AsyncComponent[]> = new ReplaySubject<
    AsyncComponent[]
  >(1);

  // observable emits false when components are loading, and true when rendered
  // see this explanation of logic: https://stackoverflow.com/a/54543327
  private asyncComponentsGate$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true
  );

  // observable emits false when images are loading, and true when loaded (or error loading)
  // see this explanation of logic: https://stackoverflow.com/a/54543327
  private imagesReadyGate$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true
  );

  // static copy of components
  private pageComponents: AsyncComponent[] = [];

  private requests: HttpRequest<any>[] = [];
  private loadedSections: ComponentLoaded = {
    overview: false,
    portfolio: false,
    performance: false,
    pricing: false,
    distributions: false,
    documents: false,
    ['press-releases']: true,
  };
  private componentLoaded$ = new BehaviorSubject<ComponentLoaded>(
    this.loadedSections
  );

  constructor(@Inject(DOCUMENT) private documentRef: Document) {
    this.pageComponents$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((cmps: AsyncComponent[]): void => {
        this.pageComponents = cmps;
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * components should call this when they are created
   */
  public registerComponents(page: Page): void {
    logger.debug('components registered', page);
    const rootCmp: BrComponent = page.getComponent();
    const pageComponents: AsyncComponent[] = this.mapComponent(rootCmp);
    logger.debug('mapped components', pageComponents);
    this.pageComponents$.next(pageComponents);
  }

  /**
   * components should call this when they have rendered on the page
   */
  public update(
    id: string,
    status: RenderingStatus,
    isAsync?: boolean,
    sectionId?: string
  ): void {
    logger.debug('component update', id, status, isAsync, sectionId);
    const cmp: AsyncComponent = this.pageComponents.find(
      (pageCmp: AsyncComponent): boolean => pageCmp.id === id
    );
    if (cmp) {
      cmp.status = status;
      if (isAsync) {
        cmp.isAsync = isAsync;
      }
      if (sectionId) {
        cmp.sectionId = sectionId;
      }
      if (status === RenderingStatus.READY) {
        cmp.readyTime = Date.now();
        cmp.duration = cmp.readyTime - cmp.startTime;
      }
      this.pageComponents$.next(this.pageComponents);
    }
  }

  /**
   * components should call this when they are created
   */
  public register(cmpId: string, sectionId?: string): void {
    logger.debug('component registered', cmpId, sectionId);
  }

  /**
   * components should call this when they have rendered on the page
   */
  public ready(cmpId: string): void {
    logger.debug('component ready', cmpId);
  }

  /**
   * components should call this when they are destroyed
   */
  public remove(cmpId: string): void {
    logger.debug('component removed', cmpId);
  }

  public containerStatus(status: string): void {
    logger.debug('container status', status);
  }

  public getPageStatus$(): Observable<RenderingStatus> {
    return this.pageComponents$.pipe(
      map(
        (pageComponents: AsyncComponent[]): RenderingStatus =>
          pageComponents.some(
            (cmp: AsyncComponent): boolean =>
              cmp.status === RenderingStatus.IN_PROGRESS
          )
            ? RenderingStatus.IN_PROGRESS
            : RenderingStatus.READY
      )
    );
  }

  public getPageComponentsStatus$(): Observable<AsyncComponent[]> {
    return this.pageComponents$.pipe(
      map((pageComponents: AsyncComponent[]): AsyncComponent[] =>
        pageComponents.sort((a: AsyncComponent, b: AsyncComponent): number =>
          a.status.localeCompare(b.status)
        )
      )
    );
  }

  public getAsyncComponentsReady$(): Observable<boolean> {
    return this.asyncComponentsGate$;
  }

  public getImagesReady$(): Observable<boolean> {
    return this.imagesReadyGate$;
  }

  private mapComponent = (cmp: BrComponent): AsyncComponent[] => {
    const isAsync: boolean = ASYNC_COMPONENTS.includes(cmp.getName());
    const asyncCmp: AsyncComponent = {
      id: cmp.getId(),
      name: cmp.getName(),
      isAsync,
      status: isAsync ? RenderingStatus.IN_PROGRESS : RenderingStatus.READY, // assume sync component, so ready by default
      startTime: Date.now(),
    };
    const children: AsyncComponent[] =
      cmp.getChildren().flatMap(this.mapComponent) || [];
    return [asyncCmp].concat(children);
  };

  public checkImages(): void {
    const images: HTMLImageElement[] = Array.from(
      this.documentRef.querySelectorAll<HTMLImageElement>('img')
    );
    const loadingImages: HTMLImageElement[] = images.filter(
      (img: HTMLImageElement): boolean => !img.complete
    );
    logger.debug('checkImages()', images, loadingImages);
    this.imagesReadyGate$.next(loadingImages.length <= 0); // not ready if 1 or more incomplete images

    // this listens to 'load' and 'error' events on all loading images
    // forkJoin means subscribe() won't run until ALL images are complete
    forkJoin(
      loadingImages.map((img: HTMLImageElement) =>
        merge(fromEvent(img, 'load'), fromEvent(img, 'error')).pipe(first())
      )
    ).subscribe((result) => {
      logger.debug('combined image loading result', result);
      this.imagesReadyGate$.next(true); // images ready as all complete
    });
  }

  ///////////////////////////
  // original product page request checking
  ///////////////////////////

  public addRequest(req: HttpRequest<any>) {
    this.requests.push(req);
  }

  private requestCheck() {
    const commonRequests = this.requests.findIndex(
      (req) =>
        req?.body?.operationName === 'Labels' ||
        req?.body?.operationName === 'ProductDetail'
    );
    const overviewRequests = this.requests.findIndex(
      (req) => req?.body?.operationName === 'FundSummary'
    );
    const portfolioRequests = this.requests.findIndex(
      (req) => req?.body?.operationName === 'PortfolioStatistics'
    );
    const performanceRequests = this.requests.findIndex(
      (req) =>
        req?.body?.operationName === 'Cumulative' ||
        req?.body?.operationName === 'Annualized' ||
        req?.body?.operationName === 'CalendarYear' ||
        req?.body?.operationName === 'Discrete' ||
        req?.body?.operationName === 'RiskStatsSummary' ||
        req?.body?.operationName === 'RiskReturnProfile'
    );
    const distributionsRequests = this.requests.findIndex(
      (req) => req?.body?.operationName === 'DistributionHistory'
    );
    const pricingRequests = this.requests.findIndex(
      (req) => req?.body?.operationName === 'PricesHistory'
    );
    const documentRequests = this.requests.findIndex(
      (req) => req?.body?.operationName === 'FundDocuments'
    );

    if (commonRequests !== -1) {
      this.loadedSections.overview = true;
      this.componentLoaded$.next(this.loadedSections);
    }
    if (
      commonRequests === -1 &&
      overviewRequests === -1 &&
      portfolioRequests === -1
    ) {
      this.loadedSections.portfolio = true;
      this.componentLoaded$.next(this.loadedSections);
    }
    if (
      commonRequests === -1 &&
      overviewRequests === -1 &&
      portfolioRequests === -1 &&
      performanceRequests === -1
    ) {
      this.loadedSections.performance = true;
      this.componentLoaded$.next(this.loadedSections);
    }
    if (
      commonRequests === -1 &&
      overviewRequests === -1 &&
      portfolioRequests === -1 &&
      performanceRequests === -1 &&
      distributionsRequests === -1
    ) {
      this.loadedSections.distributions = true;
      this.componentLoaded$.next(this.loadedSections);
    }
    if (
      commonRequests === -1 &&
      overviewRequests === -1 &&
      portfolioRequests === -1 &&
      performanceRequests === -1 &&
      distributionsRequests === -1 &&
      pricingRequests === -1
    ) {
      this.loadedSections.pricing = true;
      this.componentLoaded$.next(this.loadedSections);
    }
    if (
      commonRequests === -1 &&
      overviewRequests === -1 &&
      portfolioRequests === -1 &&
      performanceRequests === -1 &&
      distributionsRequests === -1 &&
      pricingRequests === -1 &&
      documentRequests === -1
    ) {
      this.loadedSections.documents = true;
      this.componentLoaded$.next(this.loadedSections);
    }
  }

  public removeRequest(req: HttpRequest<any>) {
    const i = this.requests.indexOf(req);

    if (i >= 0) {
      this.requests.splice(i, 1);
    }
    setTimeout(() => {
      this.requestCheck();
    }, 10);
  }

  public getComponentLoaded$(): Observable<ComponentLoaded> {
    return this.componentLoaded$.asObservable();
  }
}
